import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from '@/lib/api';

/**
 * Hook genérico pra labels customizados de colunas de kanban — padrão IGM.
 *
 * **Backend-first (2026-05-17):** dados vivem em `kanban_column_labels`
 * (SQLite) via `/api/v1/kanban/:workflow/columns`. Sem `localStorage` pra
 * dados de negócio (regra inviolável).
 *
 * Slug é IMUTÁVEL (chave estrutural). Label é EDITÁVEL pelo admin.
 *   - `getLabel(slug)` — sync, lê do module cache; fallback no default
 *   - `setLabel(slug, label)` — async, PUT/DELETE conforme valor
 *   - `resetLabel(slug)` — async, DELETE explícito
 *
 * O backend endpoint retorna `{labelOverrides, customColumns}` consolidados —
 * o mesmo Query é compartilhado com `useKanbanColumns` (via queryKey igual).
 */

interface BackendKanbanState {
  workflowSlug: string;
  labelOverrides: Record<string, string>;
  customColumns: BackendCustomColumn[];
}

interface BackendCustomColumn {
  slug: string;
  label: string;
  ordem: number;
  ativo: boolean;
  criadoPor: string | null;
  criadoEmIso: string;
  // BUG-V2-020/037 / Migration 045 — opcionais pra compat com backend antigo.
  variant?: string;
  isSeed?: boolean;
  // FU-4: "esta coluna representa:" (situação canônica) — null/undefined = nenhuma.
  situacaoMapeada?: string | null;
}

// ─── Module-level cache (compartilhado com useKanbanColumns) ───────────

export const _labelOverridesCache = new Map<string, Record<string, string>>();
export const _customColumnsCache = new Map<string, BackendCustomColumn[]>();

export function _hydrateKanbanCache(workflowId: string, state: BackendKanbanState): void {
  _labelOverridesCache.set(workflowId, state.labelOverrides);
  _customColumnsCache.set(workflowId, state.customColumns);
}

export function _readLabelOverrides(workflowId: string): Record<string, string> {
  return _labelOverridesCache.get(workflowId) ?? {};
}

export function _readCustomColumns(workflowId: string): BackendCustomColumn[] {
  return _customColumnsCache.get(workflowId) ?? [];
}

// ─── React Query: fetch único compartilhado ────────────────────────────

export function _useKanbanColumnsQuery(workflowId: string) {
  return useQuery({
    queryKey: ['v1', 'kanban', workflowId, 'columns'],
    queryFn: async () => {
      const state = await api.get<BackendKanbanState>(
        `/api/v1/kanban/${workflowId}/columns`
      );
      _hydrateKanbanCache(workflowId, state);
      return state;
    },
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

// ─── Hook público ──────────────────────────────────────────────────────

export function useColumnLabels(workflowId: string, defaults: Record<string, string>) {
  const qc = useQueryClient();

  const { data } = _useKanbanColumnsQuery(workflowId);
  const customLabels = data?.labelOverrides ?? _readLabelOverrides(workflowId);

  const getLabel = useCallback(
    (slug: string): string => {
      const overrides = _readLabelOverrides(workflowId);
      const custom = overrides[slug];
      if (custom && custom.trim()) return custom;
      return defaults[slug] ?? slug;
    },
    [workflowId, defaults]
  );

  const setLabel = useCallback(
    async (slug: string, label: string): Promise<void> => {
      const trimmed = label.trim();
      const invalidate = () =>
        qc.invalidateQueries({ queryKey: ['v1', 'kanban', workflowId, 'columns'] });

      if (!trimmed || trimmed === defaults[slug]) {
        try {
          await api.delete(`/api/v1/kanban/${workflowId}/columns/labels/${slug}`);
        } catch {
          // tolera 4xx — pode não existir override pra deletar
        }
        // Otimismo: limpa cache local imediato
        const overrides = { ..._readLabelOverrides(workflowId) };
        delete overrides[slug];
        _labelOverridesCache.set(workflowId, overrides);
        await invalidate();
        return;
      }

      await api.put(`/api/v1/kanban/${workflowId}/columns/labels/${slug}`, {
        label: trimmed,
      });
      // Otimismo: atualiza cache local imediato
      _labelOverridesCache.set(workflowId, {
        ..._readLabelOverrides(workflowId),
        [slug]: trimmed,
      });
      await invalidate();
    },
    [qc, workflowId, defaults]
  );

  const resetLabel = useCallback(
    async (slug: string): Promise<void> => {
      try {
        await api.delete(`/api/v1/kanban/${workflowId}/columns/labels/${slug}`);
      } catch {
        // tolera 4xx
      }
      const overrides = { ..._readLabelOverrides(workflowId) };
      delete overrides[slug];
      _labelOverridesCache.set(workflowId, overrides);
      await qc.invalidateQueries({ queryKey: ['v1', 'kanban', workflowId, 'columns'] });
    },
    [qc, workflowId]
  );

  return { customLabels, getLabel, setLabel, resetLabel };
}

// ─── Test helpers ──────────────────────────────────────────────────────

/** SÓ tests — limpa cache em memória. */
export function __resetColumnLabelsForTests(): void {
  _labelOverridesCache.clear();
  _customColumnsCache.clear();
}

/** SÓ tests — popula cache manualmente (substitui hidratação via Query). */
export function __seedColumnLabelsForTests(
  workflowId: string,
  overrides: Record<string, string>
): void {
  _labelOverridesCache.set(workflowId, overrides);
}
